<template>
  <div>
    <div class="card card-default mb-4">
      <h4 class="card-header" :class="{ 'disabled': !isMaintenance }">
        <i
          v-if="expired"
          class="fa fa-exclamation-triangle text-warning" />
          Reference: {{ quote.reference }}
      </h4>
      <div class="card-body">
        <p
          class="alert alert-warning"
          v-if="expired">
          Quote has expired
        </p>
        <p
          class="alert alert-danger"
          v-else-if="quote.approved && quote.isQuoteRejected">
          Rejected Quote, Replaced by {{quote.NewQuote ? quote.NewQuote.reference : ''}}
        </p>
        <p
          class="alert alert-success"
          v-else-if="quote.approved">
          Accepted Quote
        </p>
        <p v-if="oldQuote"
          class="alert alert-info">
          This quote is the replacement of {{oldQuote.reference}}
        </p>
        <h5 class="mb-1">
          <span v-if="quote.address">
            <i class="fas fa-home fa-fw" /> <b>Property Address:</b> {{ quote.address }}
          </span>
          <em v-else>No data available</em>
        <span v-if="quote.isPaymentDone" class="alert alert-primary" style="float:right;"><b>PAID</b></span>
        </h5>
        <p>{{ quote.QuoteType.quoteName }}</p>
        <label v-if="quote.approved" class="label label-info label-header">Approved</label>
        <label v-if="quote.approved && quote.isQuoteRejected" class="label label-danger label-header">Rejected</label>
        <label v-if="quote.approved && !quote.isReportSent && !quote.isQuoteRejected" class="label label-warning label-header">Under Progress</label>
        <label v-if="quote.approved && quote.isReportSent && !quote.isQuoteRejected" class="label label-success label-header">Completed</label>
        <br />
        <p class="mb-1">
          <i class="fas fa-clock fa-fw" /> <b>Created On:</b> {{ quote.createdAt | date_time_format }}
        </p>
        <p v-if="quote.manualRequestDone" class="mb-1">
          <template v-if="isPBGrPending" >
            <i class="fas fa-dollar-sign fa-fw" /> <b>Estimated Quote Amount:</b> {{ quote.fullCost | toDollarAmount }}
          </template>
          <template  v-else>
            <i class="fas fa-dollar-sign fa-fw" /> <b>Estimated Quote Amount:</b> {{ quote.actualCost | toDollarAmount }}
          </template>
        </p>
        <template v-if="quote.requestedPBG">
          <div class="card">
            <accordian-display
              :title="pBGTitle()"
              icon="fas fa-dollar-sign fa-fw"
              :headerClass="'card-header rounded-20'">
              <div class="card-body rounded-0">
                <p v-if="!isPBGrPending && !quote.acceptedPBG && quote.rejectionReasonPBG">
                  <strong>Reason:</strong>
                  {{ quote.rejectionReasonPBG }}
                </p>
                <p>
                  <strong>Price Beat Guarantee Proofs:</strong>
                  <ul>
                    <li v-for="(pBGAttachment, index) in quote.PBGProof" :key="index">
                      <download-input
                        :options="{ label: pBGAttachment.fileName, permalink: 'downloadFile'}"
                        :value="{ objectKey: pBGAttachment.objectKey }" />
                    </li>
                  </ul>
                </p>
              </div>
            </accordian-display>
          </div>
        </template>
        <template v-if="quote.isQuoteRejected">
          <div class="card">
            <accordian-display
              title="Rejection Reason"
              icon="fas fa-ban fa-fw"
              :headerClass="'card-header rounded-20'">
              <div class="card-body rounded-0">
                <p>
                  <strong>Reason:</strong>
                  {{ quote.rejectionReasonQ }}
                </p>
              </div>
            </accordian-display>
          </div>
        </template>
      </div>
      <div class="card-footer">
        <template v-if="quote.manualRequestDone">
          <button
            v-if="!quote.expired || quote.approved"
            :disabled="quote.approved || isPBGrPending || quote.isQuoteRejected"
            @click="acceptQuote()"
            class="my-1 btn btn-info">
            <i class="fa fa-fw fa-check-square" /> Accept Quote
          </button>
          <router-link
            v-if="expired"
            :to="'/newQuote/' + quote.uuid"
            class="my-1 btn btn-warning"
            :class="{ 'disabled': quote.NewQuoteUuid }">
            <i class="fa fa-fw fa-plus-circle" /> Request New Quote
          </router-link>
          <download-input
            :disabled="!quote.QuotePdf || isPBGrPending"
            :options="{ button: true, permalink: 'downloadFile', label: 'Download Quote' }"
            :value="{ objectKey: quote.QuotePdf ? quote.QuotePdf.objectKey : '' }" />
          <download-input
            v-if="quote.isReportSent"
            :disabled="!quote.isPaymentDone"
            :options="{ button: true, permalink: 'downloadFile', label: 'Download Report' }"
            :value="{ objectKey: quote.QuoteReport ? quote.QuoteReport.objectKey : '' }" />
          <button
            v-if="!quote.approved && !quote.expired"
            :disabled="quote.requestedPBG || quote.isQuoteRejected"
            class="my-1 btn btn-primary"
            @click="pBGRequest()">
            <i class="fa fa-fw fa-thumbs-up" /> Apply Price Beat Guarantee
          </button>
        </template>
        <p v-else-if="oldQuote"
          class="alert alert-info">
          Sorry, your previous quote {{oldQuote.reference}} had been rejected.<br>You will get the new acceptable quote within 48 hours. (For the rejection reason please navigate to {{oldQuote.reference}})
        </p>
        <p
          v-else
          class="alert alert-info">
          Thank you, we have received your query. You will get the quote within 48 hours.
        </p>
      </div>
    </div>
  </div>
</template>


<script>
  import AccordianDisplay from '@/components/displays/AccordianDisplay'
  import DownloadInput from '@/components/inputs/DownloadInput'
  import {
    ACCEPT_QUOTE,
    PBG_REQUEST
    } from '@/vuex/types'
  import Api from '@/vuex/api'
  const api = new Api()
  export default {
    name: 'QuoteCard',
    props: {
      object: {
        type: Object,
        default () {
          return {}
        }
      }
    },
    data () {
      return {
        oldQuote: null
      }
    },
    components: {
      AccordianDisplay,
      DownloadInput
    },
    computed: {
      quote () {
        return {
          ...this.object
        }
      },
      isMaintenance () {
        return this.quote.QuoteType.quoteType === 'maintenance'
      },
      expired () {
        return this.quote.expired && !this.quote.approved
      },
      isPBGrPending () {
        // If Price Guarantee Request has been lodged and it is not resolved yet.
        return this.quote.requestedPBG && !this.quote.resolvedPBG
      }
    },
    methods: {
      pBGTitle () {
        if (!this.quote.resolvedPBG) {
          return 'Price Beat Guarantee Request is in progress...'
        } else if (this.quote.acceptedPBG) {
          return 'Price Beat Guarantee Request approved. Quote price got reduced.'
        } else if (!this.quote.acceptedPBG) {
          return 'Price Beat Guarantee Request got rejected.'
        }
      },
      // Price Beat Guarantee
      async pBGRequest () {
        await this.$store.dispatch(PBG_REQUEST, this.quote.uuid)
      },
      async acceptQuote () {
        await this.$store.dispatch(ACCEPT_QUOTE, this.quote.uuid)
      },
      async fetchOldQuote () {
        let data = await api.getOldQuote(this.quote.uuid)
        this.oldQuote = data.quote
      }
    },
    async mounted () {
      await this.fetchOldQuote()
    },
    watch: {
      quote: async function (val) {
        await this.fetchOldQuote()
      }
    }
  }

</script>



// WEBPACK FOOTER //
// src/components/cards/Quote.vue